import { useQuery } from '@tanstack/react-query';
import { getAccount, getMyPermissions } from '../services/accounts.service';

/**
 * The signed-in account, including the organization the session is in (`organizationId`) and the roles
 * held there. It is the source of the active organization (see the Guard), so it is not keyed by it; an
 * organization switch reloads the page, which starts with an empty cache.
 */
export const useGetAccount = () => {
  return useQuery({
    queryKey: ['getAccount'],
    queryFn: getAccount,
    retry: (failureCount, error: any) => {
      // 401/403 are already handled by the http client's refresh-and-retry-once
      // flow (see handleAuthError in src/lib/https.ts); retrying here would just
      // re-trigger redundant refresh attempts after the user has been logged out.
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
    throwOnError: false,
  });
};

/**
 * UI permissions the signed-in user holds in the session's organization; disabled (and settled)
 * until the account has loaded.
 */
export const useGetMyPermissions = (organizationId: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: ['getMyPermissions', organizationId],
    queryFn: getMyPermissions,
    enabled,
    retry: false,
    throwOnError: false,
  });
};
